import { CircularProgress } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "components/typography";
import { Button } from "@mindlercare/ui-web";
import { styled } from "styled-components";
import CropModal from "./cropmodal";
import { UploadStatus } from "./upload.types";
import { ProgressBar } from "./ProgressBar";
import { useCropping } from "./useCropping";
import { Result } from "./result";

export type UploadProps = {
  thumbnail: JSX.Element;
  onFileAdded: (file: File) => void;
  uploadStatus: UploadStatus;
};

export const Upload = ({
  thumbnail,
  onFileAdded,
  uploadStatus,
}: UploadProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { state, clear, setCropModalVisible, getCroppedImage, onLoad } =
    useCropping();

  useEffect(() => {
    // when file uploaded successfully, clear the file input element and state to commpletely reset
    if (uploadStatus.successfulUploaded && state.file) {
      clear(fileInputRef.current);
    }
  }, [state, uploadStatus.successfulUploaded, clear]);

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const onFileInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCropModalVisible(true);

    if (uploadStatus.uploading) return;
    const { files } = event.target;

    if (files) {
      addFile(files[0]);
    }
  };

  const addFile = (file: File) => {
    if (!file) return;
    const reader = new FileReader();

    reader.onload = onLoad;
    reader.readAsDataURL(file);
  };

  const onCropSaved = async (cropped: string) => {
    const image = await getCroppedImage(cropped);
    onFileAdded(image);
  };

  const uploadingOrDone =
    uploadStatus.uploading || uploadStatus.successfulUploaded;

  return (
    <>
      <CropModal
        open={state.showCropModal}
        photoURL={state.selectedImageSrc as string}
        onSave={onCropSaved}
        onClose={() => {
          clear(fileInputRef.current);
          setCropModalVisible(false);
        }}
        onUploadNew={openFileDialog}
      />
      <Column>
        <Content>
          <DropZone
            isHighlighted={state.highlight}
            uploadingOrDone={uploadingOrDone}
            onClick={openFileDialog}
          >
            <FileInput
              accept={".png, .jpg, .jpeg"}
              ref={fileInputRef}
              type="file"
              onChange={onFileInputChanged}
            />
            <ImageContainer>
              {state.selectedImageSrc ? (
                <SelectedImg alt={""} src={state.selectedImageSrc as string} />
              ) : (
                thumbnail
              )}
            </ImageContainer>
          </DropZone>
          <Files>
            {state.file && (
              <ProgressBar file={state.file} uploadStatus={uploadStatus} />
            )}

            {state.file ? (
              <Button
                variant="secondary"
                onClick={() => clear(fileInputRef.current)}
              >
                {t("CLEAR")}
              </Button>
            ) : (
              <Button variant="secondary" onClick={openFileDialog}>
                {t("CHOOSE")}
              </Button>
            )}
          </Files>
          {state.errorMessage && (
            <ErrorMessage>{state.errorMessage}</ErrorMessage>
          )}
          {uploadStatus.uploading && (
            <SpinnerWrapper>
              <CircularProgress
                style={{
                  width: "15px",
                  height: "15px",
                  display: "inline-block",
                }}
              />
              <InformingText>{uploadStatus.uploadingMessage}</InformingText>
            </SpinnerWrapper>
          )}
        </Content>

        {(state.errorMessage || uploadStatus.uploadingMessage) && (
          <Result successs={false} />
        )}

        {uploadStatus.successfulUploaded && <Result successs={true} />}

        <Typography variant="body-small" style={{ marginTop: "24px" }}>
          {state.errorMessage || uploadStatus.uploadingMessage}
        </Typography>
      </Column>
    </>
  );
};

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  width: 100%;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const DropZone = styled.div<{
  isHighlighted: boolean;
  uploadingOrDone: boolean;
}>`
  height: 300px;
  width: 300px;
  border: 2px dashed $transparentGreen;
  border-radius: 5%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-size: 16px;
  overflow: hidden;
  flex-shrink: 0;

  background-color: ${(props) =>
    props.isHighlighted ? "rgb(188, 185, 236)" : "#fff"};
  cursor: ${(props) => (props.uploadingOrDone ? "default" : "pointer")};
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: auto;
  padding-left: 8px;
`;

const InformingText = styled.div`
  font-size: $small-font-size;
  color: $darkGreen;
  display: inline-block;
  padding-left: 10px;
  width: 150px;
  margin: 0;
`;

const Files = styled.div`
  margin-left: 32px;
  justify-items: flex-start;
  flex: 1;
  overflow-y: auto;
  min-width: 140px;
  display: flex;
  align-items: center;
  position: relative;
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 10px;
  width: 40%;
  margin: auto;
  flex: 2;
`;

const FileInput = styled.input`
  display: none;
`;

const SelectedImg = styled.img`
  max-width: 300px;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
